export const enTranslations = {
  navbar: {
    about: 'About',
    contact: 'Contact',
    language: 'Language'
  },
  contactPage: {
    title: 'Contact Me',
    subtitle: 'Feel free to reach out to me by filling out the form below.',
    nameLabel: 'Name',
    emailLabel: 'Email',
    subjectLabel: 'Subject',
    messageLabel: 'Message',
    required: '*',
    namePlaceholder: 'Your Name',
    emailPlaceholder: 'your.email@example.com',
    subjectPlaceholder: 'Subject',
    messagePlaceholder: 'Your message...',
    sendButton: 'Send Message',
    sendingButton: 'Sending...',
    errorEmpty: 'Please fill in all fields.',
    errorEmail: 'Please enter a valid email address.',
    successMsg: 'Message sent successfully!',
    failMsg: 'Failed to send message. Please try again later.'
  },
  footer: {
    aboutMe: 'About me',
    contact: 'Contact',
    language: 'Language'
  },
  footerInfo: {
    legal: 'Legal',
    privacy: 'Privacy',
    copyright: '© 2025 Gianni Carozza. All rights reserved.'
  },
  homePage: {
    firstLine: '"Hello World", I am ',
    secondLine: 'Gianni Carozza',
    description: "passionate software developer.\nWhere imagination inspires innovation.",
    location: '📍 Munich, Germany',
    universityProjects: 'University Projects',
    universitySubtitle: 'Projects that were created as part of my studies',
    personalProjects: 'Personal Projects',
    personalSubtitle: 'Because I love creating',
    cta: "Let's bring ideas to life!",
    contact: 'Contact me'
  },
  uniProjectsPage: {
    title: 'University Projects',
    lines: [
      'This is where ideas become reality.',
      'With passion and an eye for detail,',
      'I develop apps that inspire users',
      'and create added value.'
    ],
    placeholderLabel: 'Project Name',
    project1: "Projectmodule Web",
    project2: "Generative Design x AI",
    project3: "Mobile Applications",
    project4: "Personal Health x AI",
  },
  personalProjectsPage: {
    title: 'Personal Projects',
    lines: [
      'This is where ideas become reality.',
      'With passion and an eye for detail,',
      'I develop apps that inspire users',
      'and create added value.'
    ],
    placeholderLabel: 'Project Name',
    project1: "Circa Quiz",
    project2: "Paper Soccer",
    project3: "Dot 2D",
  },
  generativeDesignXAIPage: {
    heroTitle: 'Weather Fingerprint',
    heroSubtitle: 'Generative Design of Climate Data Project',
    heroCodedIn: 'Coded in Java',
    summaryTitle: 'Summary',
    conceptTitle: 'Concept & Technical Approach',
    designTitle: 'Design Process & Learnings',
    moreThanCities: 'More than 100 Cities',
    generatorTitle: '2D & 3D Models Generator',
    modelButton: 'Create 3D model',
    howFingerprintTitle: 'How the Fingerprint Pattern is Created',
    leggobrickTitle: 'How the LEGGO Brick is Created',
    modelCarouselTitle: '3D Models Carousel',
    
    // Tab contents
    summaryContent: [
      "A creative data visualization project that transforms weather information into unique visual \"fingerprints\" using generative design techniques.",
      "By converting meteorological data from different cities into graphical representations, the project explores the intersection of data, design, and technology."
    ],
    
    conceptContent: {
      parametersIntro: "The project uses three key weather parameters to create a distinctive visual representation:",
      parameters: [
        "Red: Temperature (°C)",
        "Blue: Humidity (%)",
        "White: Wind Speed (km/h)"
      ],
      innovationsTitle: "Key Innovations",
      innovations: [
        "Utilizing Perlin Noise for agent-based pattern generation",
        "Implementing a color-coded system to represent weather intensities",
        "Developing a method to create 3D-like visualizations"
      ],
      sliderLabels: {
        temperature: "Temperature:",
        humidity: "Humidity:",
        windSpeed: "Wind Speed:"
      }
    },
    
    designContent: {
      processTitle: "Process",
      processList: [
        "Generate unique weather fingerprint patterns",
        "Adapt pattern based on location-specific weather data",
        "Create a visual with varying bar heights and colors",
        "Fuse elements into a cohesive \"LEGGO Brick\" design"
      ],
      learningsTitle: "Learnings",
      learningsList: [
        "Explored ways to make weather data more engaging",
        "Experimented with different visualization techniques",
        "Developed method to compress complex data into intuitive graphics"
      ]
    },
    
    steps: {
      fingerprint: {
        step1Title: 'Step 1',
        step1List: [
          'Agents are moved through the image using Perlin noise',
          'Agents draw a trail behind them'
        ],
        step2Title: 'Step 2',
        step2List: [
          'Movement of agents is centered in a random area in the middle of the screen using curl effect',
          'More distance introduced between the agents'
        ],
        step3Title: 'Step 3',
        step3List: [
          'Color legend to represent weather intensity is implemented'
        ],
        step4Title: 'Step 4',
        step4List: [
          'Real world weather intensity colors the fingerprint'
        ]
      },
      leggobrick: {
        step1Title: 'Step 1',
        step1List: [
          'Bars represent different agent areas',
          'Bar color/length is determined by the weather intensities'
        ],
        step2Title: 'Step 2',
        step2List: [
          'Camera perspective is adjusted'
        ],
        step3Title: 'Step 3',
        step3List: [
          'Bars are merged into one object'
        ],
        step4Title: 'Step 4',
        step4List: [
          'Common bottom for beams',
          'Common edge for beams',
          'Created Brick Basis'
        ],
        step5Title: 'Step 5',
        step5List: [
          'Number of bars scaled',
          'Height normalized',
          'Smoothed edges',
          'Created LEGGO Brick'
        ]
      }
    },
    leggobrickStepsTitle: 'How the LEGGO Brick is Created',
    fingerprintStepsTitle: 'How the Fingerprint Pattern is Created',
    inspectorModeOn: 'Inspector Mode: ON (Press "q" to exit)',
    inspectorModeOff: 'Inspector Mode: OFF',
    footerLinks: {
      aboutMe: 'About me',
      contact: 'Contact',
      language: 'Language'
    },
    footerInfo: {
      legal: 'Legal',
      privacy: 'Privacy',
      copyright: '© 2025 Gianni Carozza. All rights reserved.'
    }
  },
  aboutPage: {
    stepsData: [
      {
        bigHeading: 'Welcome!',
        paragraphs: [
          "Hello! I'm Gianni Carozza, a passionate developer and designer specializing in creating engaging and user-friendly digital experiences. With a strong background in computational thinking, user-centered design, and mobile applications, I strive to blend creativity with functionality in every project I undertake.",
          "My journey in design and development has equipped me with a diverse skill set, allowing me to tackle complex challenges and deliver innovative solutions. Whether it's crafting intuitive interfaces, developing robust web applications, or exploring the potentials of artificial intelligence in design, I'm always eager to learn and grow."
        ],
        skills: null,
      },
      {
        bigHeading: 'Skills',
        paragraphs: [],
        skills: [
          'HTML', 
          'CSS', 
          'JavaScript', 
          'Angular', 
          'React', 
          'TypeScript',
          'React Native', 
          'Python', 
          'SCRUM', 
          'Git', 
          'Figma', 
          'TouchDesigner'
        ],
      },
      {
        bigHeading: 'Education',
        paragraphs: [
          "I'm currently pursuing my studies in Computer Science and Design at the Munich University of Applied Sciences, where I'm delving deeper into the intersection of technology and aesthetics to create meaningful and user-friendly digital products."
        ],
        skills: null,
      },
      {
        bigHeading: 'Experience',
        paragraphs: [
          "Over the years, I have used various technologies to develop projects that bring either a vision or its mission to life. My expertise spans across different domains, including web & app development, web & app design, service design and user experience optimization."
        ],
        skills: null,
      },
    ]
  },
  privacyPage: {
    title: 'Privacy Policy',
    content: `**Privacy Policy**

Your privacy is important. This Privacy Policy explains how your personal information is being handled.

**Information Collection**

The only collect information you provide directly to us, such as when you create an account, fill out a form, or contact me.

**Use of Information**

The information is used to provide, maintain, and improve our services, and to communicate with you.

**Data Security**

Appropriate security measures are implemented to protect your data.

**Your Rights**

Under the GDPR, you have the right to access, rectify, or erase your personal data. You can also restrict or object to certain processing of your data.

**Changes to this Policy**

This Privacy Policy may be updated from time to time. You will be notified of any changes by the posting of the new policy on this page.

**Contact Us**

If you have any questions about this Privacy Policy, please contact me.`
  },
  legalPage: {
    title: 'Legal Information',
    content: `**Legal Notice**

**Information pursuant to Section 5 TMG:**

**Contact:**

Email: agianniproject@gmail.com  

**Responsible for content according to Section 55 (2) RStV:**

Gianni Carozza

**Disclaimer:**

**Liability for Content**

As service providers, we are responsible for our own content on these pages according to § 7 Abs.1 TMG. However, according to §§ 8 to 10 TMG, we as service providers are not obliged to monitor transmitted or stored external information or to investigate circumstances that indicate illegal activity.

**Liability for Links**

Our offer contains links to external websites of third parties, on whose content we have no influence. Therefore, we cannot assume any liability for these external contents.

**Copyright**

The content and works created by the site operators on these pages are subject to German copyright law. The duplication, processing, distribution, and any kind of utilization outside the limits of copyright require the written consent of the respective author or creator.

**Data Protection**

Our Privacy Policy can be found under Footer > Privacy.`
  },

  /* -------------------- NEW: CIRCA PAGE STRINGS (EN) -------------------- */
  circaPage: {
    heroTitle: 'An Engaging Quiz Experience: <br /> Anytime, Anywhere',
    tabSummary: 'Summary',
    tabConcept: 'Technology & Design',
    tabDevelopment: 'Developing Process & Learnings',

    summaryTexts: [
      `CIRCA is a cross-platform quiz app built with React Native, designed to bring
       people together for exciting trivia sessions—wherever they may be.`,
      `Combining sleek visuals with an intuitive interface, this project demonstrates
       my focus on user engagement, seamless performance, and flexible gameplay
       options that cater to diverse audiences.`
    ],
    conceptTexts: [
      `CIRCA leverages React Native to create a single codebase that operates seamlessly on both iOS and Android platforms. This approach streamlines development and ensures consistency across different devices. The application's design focuses on a lightweight structure, which facilitates fast loading times and adaptable, responsive layouts that provide a smooth user experience.`,
      `Additionally, CIRCA incorporates dynamic data management and utilizes local storage. This means the app can efficiently handle and update data in real-time while also storing necessary information directly on the user's device. As a result, CIRCA remains fully functional even without an internet connection, offering users a reliable and engaging interactive experience regardless of their connectivity status.`
    ],
    developmentTexts: [
      `Developing CIRCA with React Native presented a multitude of opportunities to enhance my expertise in cross-platform application development. I focused on creating a highly engaging and user-friendly interface by leveraging React Native's component-based architecture. Implementing responsive design principles and optimizing interaction touchpoints were critical in ensuring a seamless and intuitive user experience, catering to a diverse user base.`,
      `Throughout the development process, I encountered and overcame several challenges, such as managing complex state across multiple screens and ensuring real-time data synchronization. These experiences deepened my understanding and the importance of effective debugging techniques in a cross-device environment. Additionally, refining the application's performance and structure taught me valuable lessons in optimizing resource usage and maintaining high-quality code standards, ultimately laying a robust foundation for future mobile projects.`
    ],

    // New translations for full page support
    userFlowTitle: "User Flow",
    userFlowDesc: "CIRCA was developed with an extremely simple and fast user flow that gets players into the game with just a few taps. Everything is deliberately designed to be simple and intuitive so that groups can start answering questions immediately and have fun without unnecessary steps or interruptions.",
    flowNodes: {
      settings: "Settings",
      home: "Home screen",
      language: "Language switch",
      playerCount: "Player count",
      timer: "Timer settings",
      categories: "Category selection",
      playerCards: "Player cards",
      winner: "Winner podium"
    },
    
    typoTitle: "Typography & Colors",
    fontName: "Urbanist Bold",
    bodyText: "Body Text",
    
    processTitle: "Design Process",
    processIntro: "The design process of CIRCA focused on creating a game that is understood in seconds and is immediately fun. Every phase – from ideation to the final interface – was optimized to make interactions as light and fast as possible. Through continuous prototyping and testing, an experience was created that brings groups into the game without explanations.",
    processCards: [
      {
        title: "Understanding & Concept Definition",
        subtitle: "How to create a fast, social game?",
        text: "For CIRCA, it all started with the question of how to develop a quiz game that is <strong>immediately understood</strong>, works perfectly for groups, and is fun without explanations.<br /><br />We defined the game mechanics, analyzed typical group situations (parties, travel, shared flat evenings), and set clear goals: <strong>Speed, Simplicity, Humor, and a frictionless flow.</strong>",
        tags: ["identifying group use cases", "core interaction", "core values", "concept sketching"]
      },
      {
        title: "Research & Analysis",
        subtitle: "How to make a quiz that feels different?",
        text: "We investigated existing quiz and party games, identified typical pain points (too much text, too complicated, too slow, cluttered UI), and validated through tests with friends which types of questions and flows work fastest.<br />The goal: <strong>Zero friction – maximum Fun-Flow.</strong>",
        tags: ["wireframing", "testing question categories", "competitor analysis", "optimizing user flow"]
      },
      {
        title: "Design & Development",
        subtitle: "From concept to living experience.",
        text: "In the next step, we developed a UI design that looks fresh, friendly, and immediately understandable.<br />The focus was on ensuring that every screen can be grasped in <strong>under a second</strong> – so the group stays in the flow.",
        tags: ["UX/UI Design", "Development", "prototyping", "Visual design system", "App Store preparation", "iterative testing"]
      }
    ],

    mockupsTitle: "Mockups",
    mockupsIntro: "The mockups show how uncomplicated players are guided through CIRCA.",
    
    outroText: "Sometimes knowledge wins,<br/>sometimes intuition – but<br/>fun always wins.",

    appStoreAlt: 'Now available in App Store',

    categoriesHeader: '5 Categories',
    categoriesIntro:
      `Dive into an extensive range of trivia that covers everything from everyday curiosities
       to mind-boggling details about our world. Each category boasts over 100 meticulously
       curated questions, striking the perfect balance of fun, challenge, and discovery for
       quiz enthusiasts of all skill levels.`,

    categories: [
      {
        name: 'General',
        icon: 'general.png',
        description: `Warm up your mind with a broad range of everyday topics—perfect for kickstarting any quiz session and catching everyone's interest.`
      },
      {
        name: 'Geography',
        icon: 'geography.png',
        description: `Embark on a global tour from your couch: test your knowledge of cities, countries, and famous landmarks across all continents.`
      },
      {
        name: 'Mathematics',
        icon: 'mathematics.png',
        description: `Challenge your logic and problem-solving skills with brainteasers and calculations that range from basic operations to clever puzzles.`
      },
      {
        name: 'Nature',
        icon: 'nature.png',
        description: `Dive into the marvels of our planet's wildlife, environments, and ecosystems—from the ocean floor to mountaintops and beyond.`
      },
      {
        name: 'Science',
        icon: 'science.png',
        description: `Explore the wonders of physics, chemistry, biology, and more—pushing the limits of what you know about how our world works.`
      }
    ],

    repeatedBoxes: [
      {
        title: 'Diverse Categories for Everyone',
        content:
          `Whether you're a seasoned trivia buff or a curious newcomer, there's a
           quiz topic here to spark your interest. Explore new fields, revisit old favorites,
           and enjoy the variety that CIRCA brings to your game night.`
      },
      {
        title: 'Customizable Experience',
        content:
          `Tailor every quiz session to your group's unique preferences—select the number
           of rounds, time limits, and even personalize player names. Make your experience
           as relaxed or competitive as you want.`
      },
      {
        title: 'Multilingual Gameplay',
        content:
          `Break language barriers and invite everyone to join the fun with support
           for both English and German. It's a perfect way to host truly inclusive
           quiz sessions while sharpening your language skills.`
      }
    ],

    peopleImgAlt1: 'People using the app (left)',
    peopleImgAlt2: 'People using the app (right)'
  },
  /* -------------------- NEW: PAPER SOCCER PAGE STRINGS (EN) -------------------- */
  paperSoccerPage: {
    heroTitle: 'Paper Soccer',
    heroSubtitle: 'A Simple Yet Engaging Turn-Based Strategy Game',
    heroImageAlt: 'Paper Soccer game on mobile phone',
    techBadge: 'Built with React Native',
    
    tabOverview: 'Overview',
    tabHowToPlay: 'How to Play',
    tabRules: 'Rules',
    
    overviewTitle: 'Game Overview',
    overviewDescription: 'Paper Soccer is a simple React Native game with a grid-based interface, allowing two players to take turns selecting cells and drawing paths between them. The game combines strategy and quick thinking in a digital adaptation of the classic pen-and-paper game.',
    
    featuresTitle: 'Features',
    featuresList: [
      'Two main screens: Home screen and Game screen',
      'Grid-based gameplay with intuitive controls',
      'Player customization with color selection',
      'Editable player names',
      'Undo move functionality',
      'Clean, minimalist interface'
    ],
    
    customizationTitle: 'Customization Options',
    customizationDescription: 'Players can customize their experience by choosing colors and editing their names via simple text input.',
    
    howToPlayTitle: 'How to Play',
    howToPlaySteps: [
      'Launch the app and navigate to the Home screen',
      'Press "Start" to begin a new game',
      'Customize player names and colors if desired',
      'Players take turns selecting adjacent cells to form paths',
      'Use the undo button to revert the last move if needed',
      'The first player to reach their goal wins'
    ],
    gameplayImageAlt: 'Paper Soccer gameplay demonstration',
    
    rulesTitle: 'Game Rules',
    rulesCategories: [
      {
        title: 'Cell Selection',
        rules: [
          'Players can only select cells that are adjacent to the last selected cell',
          'Adjacency includes horizontal, vertical, and diagonal neighbors',
          'Goal cells are located at the top and bottom center of the grid',
          'Diagonal moves are only allowed in the corners'
        ]
      },
      {
        title: 'Path Formation',
        rules: [
          'Players cannot walk the same path twice',
          'Once a path between cells is taken, it cannot be reused',
          'If a player selects a cell that has already been selected or a border cell, they earn an extra move'
        ]
      },
      {
        title: 'Winning Conditions',
        rules: [
          'The objective is to navigate from the center starting point to your goal cell',
          'A player wins by reaching their goal cell with a valid move',
          'A player also wins if the opponent has no valid moves available'
        ]
      }
    ],
    
    validMoveTitle: 'Valid Move',
    invalidMoveTitle: 'Invalid Move',
    
    downloadTitle: 'Download Now',
  },

  /* -------------------- NEW: MOBILE APPLICATIONS PAGE STRINGS (EN) -------------------- */
  mobileApplicationsPage: {
    heroTitle: 'LoKI Bot',
    heroSubtitle: 'Support Chatbot for Lern-Fair',
    heroTech: 'React Native & OpenAI',
    heroImageAlt: 'LoKI Bot Mobile Application Mockup',
    
    tabOverview: 'Overview',
    tabFeatures: 'Features',
    tabDevelopment: 'Development Process',
    tabTesting: 'User Testing',
    
    overviewTitle: 'Project Overview',
    overviewParagraphs: [
      'The LoKI Bot is a support chatbot developed for Lern-Fair, providing students and helpers with a quick and intuitive way to ask questions, schedule appointments, and receive support.',
      'The mobile application combines modern technologies like React Native for the frontend and Node.js for the backend with the powerful OpenAI API to deliver an intelligent chatbot experience tailored to the specific needs of the education platform.',
      'This project showcases my focus on user-friendly, accessible, and innovative solutions in the education sector.'
    ],
    sideMenuImageAlt: 'LoKI Bot Side Menu Interface',
    
    techStackTitle: 'Technology Stack',
    techStack: [
      'React Native',
      'OpenAI API',
      'Node.js',
      'Express',
      'Expo'
    ],
    
    featuresTitle: 'Features',
    features: [
      {
        title: 'Text-based Chatbot',
        description: 'The core of the application is an intelligent chatbot that can respond to questions from students and helpers. It is trained on the specific use cases of Lern-Fair and delivers relevant information and support.'
      },
      {
        title: 'Support Ticket System',
        description: 'When the chatbot cannot answer a query, users can create a support ticket that is automatically forwarded to a human staff member.',
        images: [
          {
            src: '/MobileApplications/SupportTicket1.png',
            alt: 'Support Ticket Creation',
            caption: 'Support Ticket Creation'
          },
          {
            src: '/MobileApplications/SupportTicket2.png',
            alt: 'Support Ticket Response',
            caption: 'Support Ticket Response'
          }
        ]
      },
      {
        title: 'Learning Pair Calendar',
        description: 'An integrated calendar allows users to plan, confirm, or cancel appointments for learning sessions - directly through the chatbot.',
        images: [
          {
            src: '/MobileApplications/CalendarFeature.png',
            alt: 'Learning Pair Calendar Feature',
            caption: 'Interactive Calendar for Appointment Management'
          }
        ]
      },
      {
        title: 'Audio Messages',
        description: 'Users can record audio messages that are automatically transcribed and processed by the chatbot - a convenient alternative to text input.'
      },
      {
        title: 'Message Interactions',
        description: 'A dropdown menu for messages enables various actions like editing, deleting, or copying texts.'
      }
    ],
    
    developmentTitle: 'Development Process',
    agileTitle: 'Agile Approach',
    agileDescription: 'The project was implemented in three sprints using an agile approach to enable continuous improvement and adaptation. I used GitLab for task management and version control.',
    
    sprints: [
      {
        title: 'Sprint 1 - Setup',
        tasks: [
          'Development environment setup',
          'Backend basic structure with Node.js',
          'Frontend framework with React Native',
          'Initial OpenAI API integration'
        ]
      },
      {
        title: 'Sprint 2 - Integration',
        tasks: [
          'Chat interface implementation',
          'Calendar component for learning pairs',
          'Support ticket system',
          'Audio transcription'
        ]
      },
      {
        title: 'Sprint 3 - Testing & Deployment',
        tasks: [
          'Usability testing',
          'Bug fixes and optimizations',
          'UI improvements',
          'Preparation for deployment'
        ]
      }
    ],
    
    epicsTitle: 'Epics and User Stories',
    epicsDescription: 'The development was divided into different epics, each covering specific functionalities and user stories:',
    epics: [
      'Chatbot development and integration',
      'User interface and user experience',
      'Chatbot training for Lernfair use cases',
      'Communication, connectivity, and quality assurance'
    ],
    
    testingTitle: 'User Testing',
    methodologyTitle: 'Methodology',
    methodologyDescription: 'I applied a combination of usability testing and contextual inquiry, where test subjects were placed in a realistic usage situation. The goal was to verify the user-friendliness and functionality of the support chatbot.',
    
    tasksTitle: 'Test Tasks',
    tasks: [
      'Ask general questions to the chatbot',
      'Request a support staff member',
      'Edit messages',
      'Schedule a learning appointment',
      'Cancel a learning appointment'
    ],
    
    resultsTitle: 'Results',
    resultCategories: [
      {
        title: 'Design and Usability',
        items: [
          'Better color highlighting needed for selected calendar dates',
          'Remove black border on personal messages',
          'More left and right spacing needed for calendar',
          'Adjust colors for confirmation and cancel buttons'
        ]
      },
      {
        title: 'Functionality',
        items: [
          'Appointment booking was rated as good and useful',
          'Chatbot responses should be deleted when the associated request is deleted',
          'The ability to reference an answer was positively received',
          'Improvement needed for the support request functionality',
          'Text should follow the keyboard'
        ]
      },
      {
        title: 'Language Support',
        items: [
          'User interface should be translated to German'
        ]
      }
    ],
    
    conclusionTitle: 'Conclusion',
    conclusionText: 'The testing provided valuable insights into the usability and functionality of the LoKI Bot. The feedback and observations I collected served as a basis for further optimizations and ensured that the chatbot optimally meets user needs. I implemented the identified improvement suggestions for the final version.'
  },

  /* -------------------- NEW: PERSONAL HEALTH AI PAGE STRINGS (EN) -------------------- */
  personalHealthAIPage: {
    heroTitle: 'DODONT',
    heroSubtitle: 'Digital Health - Supporting conscious and healthy smartphone usage in everyday life',
    techBadge: 'React Native & AI',
    
    tabSummary: 'Summary',
    tabConcept: 'Concept & Approach',
    tabDevelopment: 'Development Process',
    
    summaryTexts: [
      'DODONT is a digital health application designed to help users develop a conscious and healthy relationship with their smartphones. The project addresses the growing concern of excessive screen time and its negative effects on mental health and well-being.',
      'By combining behavioral science insights with modern technology, DODONT provides personalized support for users who want to regain control over their digital habits without completely disconnecting from the digital world.'
    ],
    
    conceptTexts: [
      'The concept behind DODONT is rooted in behavioral psychology and user-centered design. We identified that the target audience isn\'t limited by demographics but by behavior - anyone who finds themselves unconsciously reaching for their phone can benefit from our solution.',
      'Our approach focuses on awareness rather than restriction. Instead of blocking apps or setting strict limits, DODONT helps users understand their usage patterns and make informed decisions about their digital consumption.'
    ],
    
    developmentTexts: [
      'The development process followed an iterative design approach, starting with extensive user research and competitive analysis. We conducted interviews with potential users to understand their pain points and motivations regarding smartphone usage.',
      'The technical implementation includes a React Native mobile application with an engaging onboarding experience, complemented by a landing page that communicates the project\'s mission and values effectively.'
    ],
    
    problemTitle: 'What You Should Know',
    sourceLink: 'View Study',
    
    statistics: [
      {
        chartImage: '/PersonalHealthXAI/bar-chart1.png',
        title: 'Growth in Entertainment Screen Time',
        description: 'Between 2019 and 2022, entertainment screen time for teenagers (13-19 years) increased by over an hour, reaching 8 hours and 39 minutes per day.',
        sourceLink: 'https://www.commonsensemedia.org/sites/default/files/research/report/8-18-census-integrated-report-final-web_0.pdf'
      },
      {
        chartImage: '/PersonalHealthXAI/bar-chart2.png',
        title: 'Smartphone Usage by Age Group',
        description: 'Young people aged 18-24 are significantly more likely to check their smartphone right after waking up (62%) and use calming apps (50%) compared to older age groups.',
        sourceLink: 'https://www.deloitte.com/de/de/Industries/tmt/research/smartphone-nutzung-2024.html'
      },
      {
        chartImage: '/PersonalHealthXAI/bar-chart3.png',
        title: 'Negative Effects of Smartphone Usage',
        description: 'Almost half of young adults (48%) report going to sleep later than intended due to smartphone use, with many also feeling distracted (40%) and compelled to check their devices frequently (55%).',
        sourceLink: 'https://www.deloitte.com/de/de/Industries/tmt/research/smartphone-nutzung-2024.html'
      }
    ],
    
    audienceTitle: 'Behavior-Based Target Audience',
    audienceDescription: 'Today, anyone can be affected by smartphone overuse - whether commuting on the train or in other public transport. The omnipresent use of smartphones makes it easy to unconsciously spend more time on the screen and ultimately experience negative consequences for well-being.',
    audienceImageAlt: 'People using phones on public transport',
    
    topicsTitle: 'Understanding Digital Wellness',
    topicsSubtitle: 'Explore key aspects of how smartphone usage impacts our daily lives and well-being',
    
    topicItems: [
      {
        image: '/PersonalHealthXAI/bed.png',
        title: 'Sleep Disruption & Screen Time',
        description: 'Excessive smartphone use before bed disrupts natural sleep patterns. The blue light from screens interferes with melatonin production, making it harder to fall asleep and reducing overall sleep quality.'
      },
      {
        image: '/PersonalHealthXAI/overload.png',
        title: 'Digital Overload & Mental Fatigue',
        description: 'Constant exposure to notifications and digital content creates cognitive overload. This leads to mental exhaustion, reduced focus, and increased stress levels throughout the day.'
      },
      {
        image: '/PersonalHealthXAI/parent.png',
        title: 'Family Dynamics & Screen Time',
        description: 'Balancing family time with device usage has become a modern challenge. Parents struggle to set healthy boundaries while managing their own digital habits and being present for their children.'
      },
      {
        image: '/PersonalHealthXAI/bench.png',
        title: 'Social Connection in the Digital Age',
        description: 'While digitally connected, many people feel increasingly isolated. Physical presence doesn\'t guarantee real connection when everyone is absorbed in their devices, creating a paradox of being together yet alone.'
      }
    ],
    
    onboardingTitle: 'Interactive Onboarding Experience',
    onboardingDescription: 'Try our onboarding prototype - an engaging introduction that educates users about digital wellness through an interactive quiz',
    
    onboardingScreens: {
      welcome: {
        title: 'Welcome',
        text1: 'We are here to support you on your journey to a balanced digital life.',
        text2: 'Take control of your social media habits and reclaim your time.',
        button: "Let's Go"
      },
      q1: {
        question: 'After how many hours of recreational screen time per week is one considered addicted?',
        options: ['30 hours', '35 hours', '40 hours', '45 hours']
      },
      q2: {
        question: 'How many people have experienced negative consequences from smartphone use?',
        options: ['23 percent', '35 percent', '43 percent', '56 percent']
      },
      q3: {
        question: 'An average user in Germany unlocks their phone every ...',
        options: ['18 minutes', '24 minutes', '27 minutes', '33 minutes']
      },
      results: {
        title: 'Your Answers',
        q1Label: 'Question 1: Screen time addiction threshold',
        q2Label: 'Question 2: Negative consequences',
        q3Label: 'Question 3: Average unlock frequency',
        yourAnswer: 'Your answer:',
        correctAnswer: 'Correct answer:',
        q1Correct: '35 hours',
        q2Correct: '43 percent',
        q3Correct: '18 minutes',
        noAnswer: 'No selection',
        restartBtn: 'Try Again'
      },
      timeInput: {
        question: 'How many hours do you spend on your phone daily?',
        unit: 'hours'
      },
      visualization: {
        subtitle: 'Based on your input, you would spend',
        yearsLabel: 'years',
        description: 'of your life on your phone',
        lifeLegend: 'Life',
        socialMediaLegend: 'Social Media'
      },
      continueBtn: 'Continue'
    }
  },

  /* -------------------- PROJECT MODULE WEB PAGE STRINGS (EN) -------------------- */
  projectModuleWebPage: {
    hero: {
      backgroundImage: '/ProjectModuleWeb/delphi-bg.png',
      heroImage: '/ProjectModuleWeb/delphi-figure.png',
      title: 'Delphi',
      subtitle: 'Your Personal Study Guide',
      tech: 'React Project',
      figureAlt: 'Delphi Funko-style figure'
    },
    tabs: {
      summary: 'Summary',
      concept: 'Concept & Technical Approach',
      challenges: 'Challenges & Learnings'
    },
    summaryText: [
      "This project focuses on creating a digital platform that bridges the gap between students and study guidance. By leveraging user interviews and structured insights, we aim to provide personalized solutions for educational decision-making.",
      "The scope image highlights the project’s complexity, showcasing core deliverables like the landing page, profile/help section, and AI-powered chat. Each component posed unique technical and design hurdles, but the iterative approach ensured alignment with user needs."
    ],
    conceptText: [
      "The concept is built around three pillars: Simplicity, Reliability, and Guidance. These principles ensure the platform remains intuitive, trustworthy, and supportive for its users. The technical foundation includes interactive features like a chat system powered by trained AI.",
      "The hexagonal diagram in the image visually represents the project’s key values, emphasizing their interconnected nature and the holistic approach to solving student challenges."
    ],
    challengesText: [
      "One of the main hurdles was ensuring the user interface remains simple while accommodating diverse functionality. Integrating feedback into iterative design cycles helped refine both aesthetics and usability.",
      "The image illustrates a key insight as well as the core philosophy of the project: understanding customer needs deeply. It reflects the foundational belief that meaningful products and services are born from empathy and insight into user experiences."
    ],
    tabImages: {
      summary: '/ProjectModuleWeb/summary_w.png',
      concept: '/ProjectModuleWeb/concept_w.png',
      challenges: '/ProjectModuleWeb/challenges_w.png'
    },
    process: {
      title: 'Process',
      phases: [
        {
          title: 'Phase 1 - Research',
          content: "In the first phase, we focused on identifying the core audience through stakeholder mapping and interview preparation. Key tasks included developing a comprehensive stakeholder map, preparing an interview guide tailored to multiple categories of students as well as conductiong the interview, and conducting desk research to analyze existing data on educational decision-making challenges. The research highlighted common issues, such as insufficient guidance and lack of structured information, which became the foundation for subsequent phases.",
          pdf: '/ProjectModuleWeb/phase1.pdf'
        },
        {
          title: 'Phase 2 - Synthesis',
          content: "During this phase, we synthesized interview data and desk research findings into actionable insights. Key tasks involved categorizing data, creating personas to represent diverse user groups, and drafting a concept document to address identified pain points. This phase emphasized the need for a personalized and intuitive solution that bridges the gap between theoretical knowledge and practical application.",
          pdf: '/ProjectModuleWeb/phase2.pdf'
        },
        {
          title: 'Phase 3 - Ideation',
          content: "The third phase focused on generating innovative ideas and designing initial prototypes. Tasks included brainstorming sessions using the 'How Might We' framework, developing flowcharts for user interaction paths, and sketching pre-wireframe designs for key screens. The emphasis was on translating research insights into a functional, visually appealing interface that prioritizes user needs.",
          pdf: '/ProjectModuleWeb/phase3.pdf'
        },
        {
          title: 'Phase 4 - Prototyping',
          content: "In the final phase, we developed a high-fidelity prototype and conducted iterative testing. Key activities included integrating feedback from usability tests, implementing design refinements, and validating the prototype using MeCUE evaluation metrics. The deliverable was a polished, user-tested product that aligns with the project's objectives and ensures a seamless user experience.",
          pdf: '/ProjectModuleWeb/phase4.pdf'
        }
      ]
    }
  }
};
